import Phaser from "phaser";
import { COLORS, hexToPhaser } from "./colors";
import { playSound } from "../utils/sound-utils";
import { SpinningWheel } from "./spinning-wheel";
import { SpriteLoader } from "../sprite-loader";
import { ModernButton } from "./modern-button";
import { WheelPrize, ItemDefinition } from "../models/common";

interface DailyRewardResponse {
  success: boolean;
  error?: string;
  alreadySpun?: boolean;
  items?: ItemDefinition[];
  reward?: {
    type: "item" | "xp" | "none";
    itemDef?: ItemDefinition;
    xpAmount?: number;
    newLevel?: number;
    newXP?: number;
  };
  droppedToGround?: boolean;
}

export interface DailyRewardScreenOptions {
  onClose?: () => void;
  showBigMessage?: boolean; // If true, show big "YOUR DAILY REWARD!" message
  showWheel?: boolean; // If true, show spinning wheel
  spinNumber?: number; // The spin number (1, 3, 5 for Reward1, Reward2, Reward3)
  showNoneSlots?: boolean; // If false, wheel shows only item prizes with no "none" gaps (default: true)
  socketEvent?: string; // Socket event name to use (default: "request-daily-reward")
  title?: string; // Screen title text (default: "DAILY REWARD")
  bgKey?: string; // Background image texture key (default: "reward-bg")
}

export class DailyRewardScreen {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container | null = null;
  private bg: Phaser.GameObjects.Graphics | null = null;
  private bgImage: Phaser.GameObjects.Image | null = null;
  private onClose: (() => void) | null = null;
  private activeTweens: Phaser.Tweens.Tween[] = [];
  private spinningWheel: SpinningWheel | null = null;
  private windowManager: any = null; // Reference to window manager to hide UI
  private spinButton: ModernButton | null = null;
  private rewardDisplayObjects: Phaser.GameObjects.GameObject[] = [];
  private titleText: Phaser.GameObjects.Text | null = null;
  private serverRewardResponse: any = null; // Store server response to check if item was dropped
  private sunRays: Phaser.GameObjects.Graphics | null = null; // Sun rays behind the wheel
  private rewardMusic: Phaser.Sound.BaseSound | null = null; // Background music for reward screen

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  static preload(scene: Phaser.Scene) {
    scene.load.image("reward-bg", "/sprites/reward-bg.png");
    scene.load.audio("reward-music", "/music/reward.mp3");
  }

  setWindowManager(windowManager: any) {
    this.windowManager = windowManager;
  }

  private ignoreFromMainCamera(obj: Phaser.GameObjects.GameObject | null) {
    if (obj && this.scene.cameras.cameras.length > 1) {
      this.scene.cameras.main.ignore(obj);
    }
  }

  show(options?: DailyRewardScreenOptions) {
    const {
      onClose = null,
      showBigMessage = false,
      showWheel = false,
      spinNumber = 1,
      showNoneSlots = true,
      socketEvent = "request-daily-reward",
      title = "DAILY REWARD",
      bgKey = "reward-bg",
    } = options || {};

    // Play reward music when screen is shown
    this.playRewardMusic();

    if (showBigMessage) {
      return this.showBigMessage(onClose, bgKey);
    }

    if (showWheel) {
      return this.showWheelReward(onClose, spinNumber, showNoneSlots, socketEvent, title, bgKey);
    }

    this.showRegularReward(onClose, bgKey);
  }

  private playRewardMusic() {
    if (this.rewardMusic && this.rewardMusic.isPlaying) {
      return; // Already playing
    }

    if (this.scene.cache.audio.exists("reward-music")) {
      const sound = this.scene.sound.play("reward-music", {
        volume: 0.6,
        loop: true,
      });
      if (typeof sound !== "boolean") {
        this.rewardMusic = sound;
      }
    }
  }

  private stopRewardMusic() {
    // Stop the reward music by key
    this.scene.sound.stopByKey("reward-music");
    if (this.rewardMusic) {
      this.rewardMusic = null;
    }
  }

  private showBigMessage(onClose: (() => void) | null, bgKey: string = "reward-bg") {
    if (this.container) {
      return; // Already visible
    }

    this.onClose = onClose || null;
    const { width, height } = this.scene.scale;

    // Background image
    this.bgImage = this.scene.add.image(width / 2, height / 2, bgKey);
    this.bgImage.setScrollFactor(0);
    this.bgImage.setDepth(200000);
    this.bgImage.setDisplaySize(width, height);
    this.ignoreFromMainCamera(this.bgImage);

    // Invisible clickable area for closing
    this.bg = this.scene.add.graphics();
    this.bg.setScrollFactor(0);
    this.bg.setDepth(200001);
    this.bg.setInteractive();

    // Hide from main camera - only render on UI camera
    this.ignoreFromMainCamera(this.bg);

    // Main container
    this.container = this.scene.add.container(width / 2, height / 2);
    this.container.setScrollFactor(0);
    this.container.setDepth(200002);

    // Hide from main camera - only render on UI camera
    this.ignoreFromMainCamera(this.container);

    // Big "YOUR DAILY REWARD!" message (similar to "FINDING MATCH" style)
    const messageText = this.scene.add.text(0, -50, "YOUR DAILY REWARD!", {
      fontSize: "64px",
      color: COLORS.WHITE,
      fontFamily: "Anton",
      align: "center",
      stroke: COLORS.BLACK,
      strokeThickness: 4,
      shadow: {
        offsetX: 0,
        offsetY: 4,
        color: "#000000",
        blur: 0,
        stroke: true,
        fill: true,
      },
    });
    messageText.setOrigin(0.5, 0.5);
    messageText.setScale(0);
    messageText.setScrollFactor(0);
    this.container.add(messageText);

    // Animate the message appearance
    const messageTween = this.scene.tweens.add({
      targets: messageText,
      scale: 1,
      duration: 600,
      delay: 300,
      ease: "Elastic.out",
    });
    this.activeTweens.push(messageTween);

    // Add a star for visual effect
    const starText = this.scene.add.text(0, 80, "★", {
      fontSize: "80px",
      color: COLORS.YELLOW,
      fontFamily: "Arial",
    });
    starText.setOrigin(0.5, 0.5);
    starText.setScale(0);
    starText.setScrollFactor(0);
    this.container.add(starText);

    // Animate star appearance
    const starTween = this.scene.tweens.add({
      targets: starText,
      scale: 1,
      duration: 400,
      delay: 800,
      ease: "Back.easeOut",
    });
    this.activeTweens.push(starTween);

    // Click anywhere to close
    this.bg.on("pointerdown", () => {
      playSound(this.scene, "menu-click-sound", null, 0.5);
      this.hide();
    });

    // Container should also be clickable to close (allow close from clicking content)
    this.container.setInteractive();
    this.container.on("pointerdown", () => {
      playSound(this.scene, "menu-click-sound", null, 0.5);
      this.hide();
    });
  }

  private async showWheelReward(
    onClose: (() => void) | null,
    spinNumber: number,
    showNoneSlots: boolean = true,
    socketEvent: string = "request-daily-reward",
    title: string = "DAILY REWARD",
    bgKey: string = "reward-bg",
  ) {
    if (this.container) {
      console.log(
        "[DailyRewardScreen] Container already exists, skipping setup",
      );
      return; // Already visible
    }

    this.onClose = onClose || null;

    const { width, height } = this.scene.scale;

    // Background image
    this.bgImage = this.scene.add.image(width / 2, height / 2, bgKey);
    this.bgImage.setScrollFactor(0);
    this.bgImage.setDepth(199999);
    this.bgImage.setDisplaySize(width, height);
    this.ignoreFromMainCamera(this.bgImage);
    this.rewardDisplayObjects.push(this.bgImage);

    // Create a full-screen blocking rectangle at very high depth to cover all UI
    const blocker = this.scene.add.rectangle(
      width / 2,
      height / 2,
      width * 2,
      height * 2,
      0x000000,
      0,
    );
    blocker.setScrollFactor(0);
    blocker.setDepth(200000); // Above most UI but below close button
    blocker.setInteractive();
    this.rewardDisplayObjects.push(blocker);

    // Invisible clickable area
    this.bg = this.scene.add.graphics();
    this.bg.setScrollFactor(0);
    this.bg.setDepth(200001);
    this.bg.setInteractive();

    // Hide from main camera - only render on UI camera
    this.ignoreFromMainCamera(this.bg);

    // Main container
    this.container = this.scene.add.container(width / 2, height / 2);
    this.container.setScrollFactor(0);
    this.container.setDepth(200002); // Increased to be on top

    // Hide from main camera - only render on UI camera
    this.ignoreFromMainCamera(this.container);

    // Title
    this.titleText = this.scene.add.text(0, -164, title, {
      fontSize: "36px",
      color: COLORS.YELLOW,
      fontFamily: "Anton",
      align: "center",
      stroke: COLORS.BLACK,
      strokeThickness: 3,
      shadow: {
        offsetX: 0,
        offsetY: 6,
        color: "#000000",
        blur: 0,
        stroke: true,
        fill: true,
      },
    });
    this.titleText.setOrigin(0.5, 0.5);
    this.container.add(this.titleText);

    // Load items data from public folder and request reward from server
    try {
      console.log("[DailyRewardScreen] Requesting reward from server...");

      // Request the reward from server FIRST
      // This applies the reward immediately, so if player closes app before spinning, they get it
      const rewardResponse = await new Promise<DailyRewardResponse>(
        (resolve) => {
          console.log(
            `[DailyRewardScreen] Socket emit for ${socketEvent}`,
          );
          (window as any).socket.emit(
            socketEvent,
            { milestone: spinNumber },
            (response: DailyRewardResponse) => {
              console.log(
                "[DailyRewardScreen] Received callback response:",
                response,
              );
              resolve(response);
            },
          );
        },
      );

      // Store the server response for later use in onSpinComplete
      this.serverRewardResponse = rewardResponse;

      if (!rewardResponse || !rewardResponse.success) {
        if (rewardResponse?.alreadySpun) {
          // Show "come back tomorrow" message in the existing container
          const msgText = this.scene.add.text(0, 0, "Come back tomorrow\nfor your next spin!", {
            fontSize: "30px",
            color: COLORS.WHITE,
            fontFamily: "Anton",
            align: "center",
            stroke: COLORS.BLACK,
            strokeThickness: 3,
          });
          msgText.setOrigin(0.5, 0.5);
          this.container?.add(msgText);
          const closeBtn = new ModernButton({
            scene: this.scene,
            x: 0,
            y: 100,
            text: "CLOSE",
            style: "success",
            onClick: () => { this.hide(); },
          });
          this.ignoreFromMainCamera(closeBtn);
          this.container?.add(closeBtn);
        } else {
          console.error(
            "[DailyRewardScreen] Failed to get reward from server:",
            rewardResponse?.error,
          );
          this.hide();
        }
        return;
      }

      console.log(
        "[DailyRewardScreen] Reward response successful, setting up wheel",
      );

      // Create wheel prizes for display using items from server
      const prizes = await this.generateWheelPrizes(rewardResponse, showNoneSlots);

      // Find which prize matches the server's selection
      let winningIndex = 0;
      if (
        rewardResponse.reward?.type === "item" &&
        rewardResponse.reward.itemDef
      ) {
        // Find the first item prize in the wheel that matches this item ID
        winningIndex = prizes.findIndex(
          (prize) =>
            prize.type === "item" &&
            prize.itemDef?.id === rewardResponse.reward?.itemDef?.id,
        );
        if (winningIndex === -1) winningIndex = 0; // Fallback
      } else if (rewardResponse.reward?.type === "none") {
        // Find the first "none" prize in the wheel
        winningIndex = prizes.findIndex((prize) => prize.type === "none");
        if (winningIndex === -1) winningIndex = 0; // Fallback
      }

      // Create rotating sun rays behind the wheel
      this.createSunRays();
      if (this.sunRays) {
        this.container.add(this.sunRays);
      }

      // Create spinning wheel with auto-start
      this.spinningWheel = new SpinningWheel(this.scene, {
        x: 0,
        y: 0,
        wheelRadius: 100,
        prizes: prizes,
        winningPrizeIndex: winningIndex,
        autoStart: true,
        onSpinComplete: (winningPrize) => {
          this.onSpinComplete(winningPrize);
        },
      });
      this.container.add(this.spinningWheel.getContainer());

      // Stop button using ModernButton
      const buttonY = 160;

      this.spinButton = new ModernButton({
        scene: this.scene,
        x: 0,
        y: buttonY,
        text: "STOP",
        style: "success",
        onClick: () => {
          playSound(this.scene, "menu-click-sound", null, 0.5);
          // Reset title text
          if (this.titleText) {
            this.titleText.setText(title);
          }
          // Hide button when stopping
          this.spinButton?.setVisible(false);
          this.spinningWheel?.stopSpin();
        },
      });
      this.ignoreFromMainCamera(this.spinButton); // Ensure button only renders on UI camera
      this.container.add(this.spinButton);

      this.spinningWheel.setSpinButton(this.spinButton as any);

      // Allow clicking on background to go back (after brief delay to prevent accidental close)
      setTimeout(() => {
        this.bg?.on("pointerdown", () => {
          if (!this.spinningWheel || !("isSpinning" in this.spinningWheel)) {
            playSound(this.scene, "menu-click-sound", null, 0.5);
            this.hide();
          }
        });
      }, 1000);

      // Prevent background click from propagating to wheel container
      this.container.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation?.();
      });
    } catch (error) {
      console.error("[DailyRewardScreen] Error loading wheel data:", error);
      console.error("[DailyRewardScreen] Stack trace:", (error as Error).stack);
      this.hide();
    }
  }

  private async generateWheelPrizes(
    rewardResponse: DailyRewardResponse,
    showNoneSlots: boolean = true,
  ): Promise<WheelPrize[]> {
    // Generate wheel prizes from server response
    const prizes: WheelPrize[] = [];

    if (rewardResponse.items) {
      for (const item of rewardResponse.items) {
        prizes.push({
          type: "item",
          itemDef: item,
        });
      }
    }

    if (!showNoneSlots) {
      // Return all items directly with no "none" gaps (used for daily spin)
      return prizes;
    }

    // Intersperse with "no reward" slots
    // Pattern: item, X, item, X, item, X
    const finalPrizes: WheelPrize[] = [];

    for (let i = 0; i < 3; i++) {
      if (i < prizes.length) {
        finalPrizes.push(prizes[i]);
      }
      finalPrizes.push({ type: "none" });
    }

    return finalPrizes;
  }

  private onSpinComplete(prize: WheelPrize) {
    playSound(this.scene, "menu-click-sound", null, 0.7);

    // Remove the spinning wheel after a brief delay to let the callback finish
    setTimeout(() => {
      if (this.spinningWheel) {
        this.spinningWheel.destroy();
        this.spinningWheel = null;
      }
      // Play extraction success sound when wheel is hidden and prize is shown
      playSound(this.scene, "extraction-success", null, 0.6);
    }, 50);

    // Show winning prize in the center with animation
    if (this.container && this.bg) {
      // Create a group for the winning prize display
      if (prize.type === "item" && prize.itemDef) {
        // Show item sprite
        const itemSprite = SpriteLoader.createSprite(
          this.scene,
          prize.itemDef.spriteSheet,
          prize.itemDef.spriteX,
          prize.itemDef.spriteY,
          this.container.x,
          this.container.y,
          true,
        );
        itemSprite.setScale(0);
        itemSprite.setDepth(200003);
        this.rewardDisplayObjects.push(itemSprite);

        // Animate item spinning in
        this.scene.tweens.add({
          targets: itemSprite,
          scale: 4,
          rotation: Math.PI * 2,
          duration: 800,
          ease: "Back.easeOut",
        });

        // Item name text
        const itemNameText = this.scene.add.text(
          this.container.x,
          this.container.y + 80,
          prize.itemDef.name || "Item",
          {
            fontSize: "32px",
            color: COLORS.WHITE,
            fontFamily: "Anton",
            align: "center",
            stroke: COLORS.BLACK,
            strokeThickness: 3,
          },
        );
        itemNameText.setOrigin(0.5, 0.5);
        itemNameText.setScrollFactor(0);
        itemNameText.setDepth(200003);
        itemNameText.setScale(0);
        this.rewardDisplayObjects.push(itemNameText);

        this.scene.tweens.add({
          targets: itemNameText,
          scale: 1,
          duration: 600,
          delay: 200,
          ease: "Back.easeOut",
        });

        // Show message if item was dropped to ground
        if (this.serverRewardResponse?.droppedToGround) {
          const dropMessage = this.scene.add.text(
            this.container.x,
            this.container.y + 120,
            "(Dropped on ground - inventory full)",
            {
              fontSize: "16px",
              color: COLORS.ORANGE,
              fontFamily: "Anton",
              align: "center",
              stroke: COLORS.BLACK,
              strokeThickness: 2,
            },
          );
          dropMessage.setOrigin(0.5, 0.5);
          dropMessage.setScrollFactor(0);
          dropMessage.setDepth(200003);
          dropMessage.setScale(0);
          this.rewardDisplayObjects.push(dropMessage);

          this.scene.tweens.add({
            targets: dropMessage,
            scale: 1,
            duration: 400,
            delay: 400,
            ease: "Back.easeOut",
          });
        }
      } else {
        // Show NO REWARD message
        const noRewardText = this.scene.add.text(
          this.container.x,
          this.container.y,
          "NO REWARD",
          {
            fontSize: "80px",
            color: COLORS.WHITE,
            fontFamily: "Anton",
            align: "center",
            stroke: COLORS.BLACK,
            strokeThickness: 4,
          },
        );
        noRewardText.setOrigin(0.5, 0.5);
        noRewardText.setScrollFactor(0);
        noRewardText.setDepth(200003);
        noRewardText.setScale(0);
        this.rewardDisplayObjects.push(noRewardText);

        this.scene.tweens.add({
          targets: noRewardText,
          scale: 1,
          rotation: Math.PI * 2,
          duration: 800,
          ease: "Back.easeOut",
        });
      }

      // Change the STOP button to CLAIM REWARD button
      if (this.spinButton) {
        this.spinButton.destroy();

        const noneLabels = [
          "FIDDLESTICKS!",
          "DANG IT",
          "AAAARGH!",
          "OH WELL",
          "@#$%&!",
        ];
        const buttonText =
          prize.type === "none"
            ? noneLabels[Math.floor(Math.random() * noneLabels.length)]
            : "CLAIM REWARD";

        // Create new CLAIM REWARD button
        this.spinButton = new ModernButton({
          scene: this.scene,
          x: 0,
          y: 160,
          text: buttonText,
          style: "success",
          onClick: () => {
            playSound(this.scene, "menu-click-sound", null, 0.5);
            this.hide();
          },
        });
        this.ignoreFromMainCamera(this.spinButton);
        this.container.add(this.spinButton);

        // Show CLAIM REWARD button after a delay
        setTimeout(() => {
          this.spinButton?.setVisible(true);
        }, 1000);
      }
    }
  }

  private showRegularReward(onClose: (() => void) | null, bgKey: string = "reward-bg") {
    if (this.container) {
      return; // Already visible
    }

    this.onClose = onClose || null;
    const { width, height } = this.scene.scale;

    // Background image
    this.bgImage = this.scene.add.image(width / 2, height / 2, bgKey);
    this.bgImage.setScrollFactor(0);
    this.bgImage.setDepth(200000);
    this.bgImage.setDisplaySize(width, height);
    this.ignoreFromMainCamera(this.bgImage);

    // Invisible clickable area for closing
    this.bg = this.scene.add.graphics();
    this.bg.setScrollFactor(0);
    this.bg.setDepth(200001);
    this.bg.setInteractive();

    // Hide from main camera - only render on UI camera
    this.ignoreFromMainCamera(this.bg);

    // Main container
    this.container = this.scene.add.container(width / 2, height / 2);
    this.container.setScrollFactor(0);
    this.container.setDepth(200002);

    // Hide from main camera - only render on UI camera
    this.ignoreFromMainCamera(this.container);

    // Title: "DAILY REWARD"
    const titleText = this.scene.add.text(0, -120, "DAILY REWARD", {
      fontSize: "48px",
      color: COLORS.YELLOW,
      fontFamily: "Anton",
      align: "center",
      stroke: COLORS.BLACK,
      strokeThickness: 3,
      shadow: {
        offsetX: 0,
        offsetY: 6,
        color: "#000000",
        blur: 0,
        stroke: true,
        fill: true,
      },
    });
    titleText.setOrigin(0.5, 0.5);
    this.container.add(titleText);

    // Stars display (5 filled stars)
    const starsStartY = -40;
    const starSize = 32;
    const starSpacing = 12;
    const totalStarsWidth = starSize * 5 + starSpacing * 4;
    const starsStartX = -totalStarsWidth / 2;

    for (let i = 0; i < 5; i++) {
      const starX = starsStartX + i * (starSize + starSpacing) + starSize / 2;
      const starText = this.scene.add.text(starX, starsStartY, "★", {
        fontSize: `${starSize}px`,
        color: COLORS.YELLOW,
        fontFamily: "Arial",
      });
      starText.setOrigin(0.5, 0.5);

      // Animate star appearance with scale and rotation
      starText.setScale(0);
      this.container.add(starText);

      // Stagger animation
      const tween = this.scene.tweens.add({
        targets: starText,
        scale: 1,
        duration: 300,
        delay: i * 100,
        ease: "Back.easeOut",
      });
      this.activeTweens.push(tween);
    }

    // Reward text
    const rewardText = this.scene.add.text(0, 50, "You've completed", {
      fontSize: "20px",
      color: COLORS.WHITE,
      fontFamily: "Anton",
      align: "center",
      stroke: COLORS.BLACK,
      strokeThickness: 2,
    });
    rewardText.setOrigin(0.5, 0.5);
    this.container.add(rewardText);

    const extractText = this.scene.add.text(
      0,
      80,
      "5 successful extractions!",
      {
        fontSize: "20px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        align: "center",
        stroke: COLORS.BLACK,
        strokeThickness: 2,
      },
    );
    extractText.setOrigin(0.5, 0.5);
    this.container.add(extractText);

    // Reward details (can be extended for actual rewards)
    const rewardDetailsText = this.scene.add.text(
      0,
      120,
      "Keep extracting for more rewards!",
      {
        fontSize: "16px",
        color: COLORS.GREEN,
        fontFamily: "Arial",
        align: "center",
        stroke: COLORS.BLACK,
        strokeThickness: 2,
      },
    );
    rewardDetailsText.setOrigin(0.5, 0.5);
    this.container.add(rewardDetailsText);

    // Close button
    const buttonWidth = 150;
    const buttonHeight = 40;
    const buttonY = 180;

    const buttonBg = this.scene.add.rectangle(
      0,
      buttonY,
      buttonWidth,
      buttonHeight,
      hexToPhaser(COLORS.GREEN),
    );
    buttonBg.setOrigin(0.5, 0.5);
    buttonBg.setStrokeStyle(2, hexToPhaser(COLORS.WHITE));
    buttonBg.setInteractive({ useHandCursor: true });
    this.container.add(buttonBg);

    const buttonText = this.scene.add.text(0, buttonY, "CONTINUE", {
      fontSize: "18px",
      color: COLORS.BLACK,
      fontFamily: "Anton",
      align: "center",
    });
    buttonText.setOrigin(0.5, 0.5);
    this.container.add(buttonText);

    // Button hover effects
    buttonBg.on("pointerover", () => {
      buttonBg.setScale(1.1);
    });

    buttonBg.on("pointerout", () => {
      buttonBg.setScale(1);
    });

    buttonBg.on("pointerdown", () => {
      playSound(this.scene, "menu-click-sound", null, 0.5);
      this.hide();
    });

    // Allow clicking anywhere on the background to close (optional)
    this.bg.on("pointerdown", () => {
      playSound(this.scene, "menu-click-sound", null, 0.5);
      this.hide();
    });

    // Prevent background click from propagating
    this.container.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation?.();
    });
  }

  private createSunRays() {
    this.sunRays = this.scene.add.graphics();
    this.sunRays.setScrollFactor(0);
    this.sunRays.setDepth(200001); // Behind the wheel
    this.ignoreFromMainCamera(this.sunRays);

    // Draw sun rays - 16 rays emanating from center, stretching beyond screen
    const numRays = 16;
    const rayLength = 1200; // Extended to go beyond screen bounds
    const rayWidth = 160;

    for (let i = 0; i < numRays; i++) {
      const angle = (i / numRays) * Math.PI * 2;
      const nextAngle = ((i + 1) / numRays) * Math.PI * 2;

      // Calculate the three points of the triangle (center and two outer points)
      const x1 = 0; // Center
      const y1 = 0;

      const x2 = Math.cos(angle - rayWidth / (2 * rayLength)) * rayLength;
      const y2 = Math.sin(angle - rayWidth / (2 * rayLength)) * rayLength;

      const x3 = Math.cos(angle + rayWidth / (2 * rayLength)) * rayLength;
      const y3 = Math.sin(angle + rayWidth / (2 * rayLength)) * rayLength;

      // Create fading effect by drawing the triangle with alpha that fades with distance
      const alpha = 0.25 - (i % 8) * 0.02; // Vary alpha for visual interest
      this.sunRays.fillStyle(0xffff00, alpha); // Bright yellow
      this.sunRays.beginPath();
      this.sunRays.moveTo(x1, y1);
      this.sunRays.lineTo(x2, y2);
      this.sunRays.lineTo(x3, y3);
      this.sunRays.closePath();
      this.sunRays.fillPath();
    }

    // Add continuous rotation tween
    if (this.sunRays) {
      const rotationTween = this.scene.tweens.add({
        targets: this.sunRays,
        rotation: Math.PI * 2,
        duration: 8000,
        repeat: -1,
        ease: "Linear",
      });
      this.activeTweens.push(rotationTween);
    }
  }

  hide() {
    console.log("[DailyRewardScreen] hide() called");
    console.log("[DailyRewardScreen] Stack trace:", new Error().stack);

    // Stop reward music
    this.stopRewardMusic();

    // Kill all active tweens
    for (const tween of this.activeTweens) {
      if (tween && tween.isActive()) {
        tween.stop();
      }
    }
    this.activeTweens = [];

    if (this.sunRays && this.sunRays.active) {
      this.sunRays.destroy();
      this.sunRays = null;
    }

    if (this.spinningWheel) {
      this.spinningWheel.destroy();
      this.spinningWheel = null;
    }

    // Destroy reward display objects (item/XP display)
    for (const obj of this.rewardDisplayObjects) {
      if (obj && obj.active) {
        obj.destroy();
      }
    }
    this.rewardDisplayObjects = [];

    if (this.spinButton) {
      this.spinButton.destroy();
      this.spinButton = null;
    }

    if (this.titleText && this.titleText.active) {
      this.titleText.destroy();
      this.titleText = null;
    }

    if (this.container && this.container.active) {
      this.container.destroy();
      this.container = null;
    }

    if (this.bg && this.bg.active) {
      this.bg.destroy();
      this.bg = null;
    }

    if (this.bgImage && this.bgImage.active) {
      this.bgImage.destroy();
      this.bgImage = null;
    }

    // Show all UI again
    if (this.windowManager) {
      this.windowManager.showAllAfterReward();
    }

    if (this.onClose) {
      this.onClose();
    }
  }

  isVisible(): boolean {
    return this.container !== null;
  }
}
